import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { FiRefreshCw, FiEye } from 'react-icons/fi';
import { fetchWebhookLogs, resendWebhook } from '../redux/slices/webhookSlice';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import Select from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { toast } from 'react-toastify';
import './WebhookLogs.css';

const WebhookLogs = () => {
  const dispatch = useDispatch();
  const { logs, logsPagination, loading } = useSelector((state) => state.webhooks);

  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [statusFilter]);

  const loadLogs = () => {
    dispatch(
      fetchWebhookLogs({
        status: statusFilter || undefined,
        page: 1,
        limit: 20
      })
    );
  };

  const handleResend = async () => {
    try {
      await dispatch(resendWebhook(selectedLog.id)).unwrap();
      toast.success('Webhook resent successfully');
      setShowResendConfirm(false);
      loadLogs();
    } catch (error) {
      toast.error(error);
    }
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
    { value: 'retrying', label: 'Retrying' }
  ];

  const columns = [
    {
      key: 'webhook',
      title: 'Webhook',
      render: (value) => (
        <div className="webhook-info">
          <span className="webhook-name">{value?.name}</span>
          <span className="webhook-project">{value?.project}</span>
        </div>
      )
    },
    {
      key: 'event',
      title: 'Event',
      render: (value) => <Badge variant="info">{value}</Badge>
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => (
        <Badge variant={value === 'sent' ? 'success' : value === 'failed' ? 'danger' : 'warning'}>
          {value}
        </Badge>
      )
    },
    {
      key: 'response_status',
      title: 'Response',
      render: (value) => (
        <span className={`response-status ${value >= 200 && value < 300 ? 'success' : 'error'}`}>
          {value || '-'}
        </span>
      )
    },
    {
      key: 'attempt_count',
      title: 'Attempts',
      render: (value) => value || 0
    },
    {
      key: 'created_at',
      title: 'Created',
      render: (value) => format(new Date(value), 'MMM dd, HH:mm:ss')
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button
            className="action-btn view"
            onClick={() => {
              setSelectedLog(row);
              setShowDetails(true);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          {(row.status === 'failed' || row.status === 'retrying') && (
            <button
              className="action-btn resend"
              onClick={() => {
                setSelectedLog(row);
                setShowResendConfirm(true);
              }}
              title="Resend"
            >
              <FiRefreshCw />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <Layout title="Webhook Logs">
      <div className="webhook-logs-page">
        <Card
          title="Webhook Delivery Logs"
          subtitle="Monitor webhook delivery status and resend failed webhooks"
          actions={
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusOptions}
              className="status-filter"
            />
          }
        >
          <Table
            columns={columns}
            data={logs}
            loading={loading}
            emptyMessage="No webhook logs found"
          />

          <Pagination
            currentPage={logsPagination.page}
            totalPages={logsPagination.pages}
            onPageChange={(page) =>
              dispatch(fetchWebhookLogs({ status: statusFilter, page, limit: 20 }))
            }
          />
        </Card>

        {/* Log Details Modal */}
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title="Webhook Log Details"
          size="large"
        >
          {selectedLog && (
            <div className="log-details">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Webhook</label>
                  <span>{selectedLog.webhook?.name}</span>
                </div>
                <div className="detail-item">
                  <label>Project</label>
                  <span>{selectedLog.webhook?.project}</span>
                </div>
                <div className="detail-item">
                  <label>Event</label>
                  <span>{selectedLog.event}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <Badge variant={selectedLog.status === 'sent' ? 'success' : 'danger'}>
                    {selectedLog.status}
                  </Badge>
                </div>
                <div className="detail-item">
                  <label>Response Status</label>
                  <span>{selectedLog.response_status || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Attempts</label>
                  <span>{selectedLog.attempt_count}</span>
                </div>
                <div className="detail-item">
                  <label>Last Attempt</label>
                  <span>
                    {selectedLog.last_attempt_at
                      ? format(new Date(selectedLog.last_attempt_at), 'MMM dd, yyyy HH:mm:ss')
                      : '-'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Created At</label>
                  <span>
                    {format(new Date(selectedLog.created_at), 'MMM dd, yyyy HH:mm:ss')}
                  </span>
                </div>
              </div>

              <div className="code-section">
                <label>Payload</label>
                <pre className="code-block">
                  {JSON.stringify(selectedLog.payload, null, 2)}
                </pre>
              </div>

              {selectedLog.response_body && (
                <div className="code-section">
                  <label>Response</label>
                  <pre className="code-block">
                    {typeof selectedLog.response_body === 'string'
                      ? selectedLog.response_body
                      : JSON.stringify(selectedLog.response_body, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.error_message && (
                <div className="error-section">
                  <label>Error Message</label>
                  <p className="error-message">{selectedLog.error_message}</p>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Resend Confirmation */}
        <ConfirmDialog
          isOpen={showResendConfirm}
          onClose={() => setShowResendConfirm(false)}
          onConfirm={handleResend}
          title="Resend Webhook"
          message="Are you sure you want to resend this webhook?"
          confirmText="Resend"
          variant="warning"
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default WebhookLogs;
